import { useState } from 'react';
import {
  Button,
  ErrorMessage,
  HStack,
  Heading,
  Pressable,
  Select,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { Box } from '@chakra-ui/react';
import { UploadIcon, XIcon } from 'lucide-react';
import { format } from 'date-fns';
import { LabOrder } from '../../../api/types';
import { formatDate } from '../utils';
import { DateField } from './DateField';
import useUploadLabResult from '../../../api/healthCoach/mutations/useUploadLabResult';

type Props = {
  userId: string;
  labOrders: LabOrder[];
  // Preselects the "Add to" order when the form is opened from a card's menu.
  initialLabId?: string;
  onClose: () => void;
};

// Sentinel for the "New result" option, kept distinct from a real order id.
const NEW_RESULT = 'new-result';

const ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png';

const Caption = ({ children }: { children: string }) => (
  <Text
    fontSize={'2xs'}
    fontWeight={700}
    color={'bluegrey'}
    textTransform={'uppercase'}
    letterSpacing={'0.04em'}
  >
    {children}
  </Text>
);

export const LabResultUploadForm = ({
  userId,
  labOrders,
  initialLabId,
  onClose,
}: Props) => {
  const [target, setTarget] = useState<string>(initialLabId ?? NEW_RESULT);
  const [file, setFile] = useState<File | null>(null);
  const [completedDate, setCompletedDate] = useState<string>(
    format(new Date(), 'yyyy-MM-dd')
  );

  const { mutation } = useUploadLabResult();

  const isNewResult = target === NEW_RESULT;
  const today = format(new Date(), 'yyyy-MM-dd');

  const pickFile = (files: FileList | null) => {
    const picked = files?.[0];
    if (picked) setFile(picked);
  };

  const handleUpload = () => {
    if (!file) return;

    mutation.mutate(
      {
        userId,
        file,
        labId: isNewResult ? undefined : target,
        labResultDateCreated: isNewResult ? completedDate || today : undefined,
      },
      {
        onSuccess: () => onClose(),
      }
    );
  };

  return (
    <VStack align={'stretch'} gap={6} w={'100%'} maxW={'720px'}>
      <Heading size={'md'} color={'secondary.500'}>
        Upload lab results
      </Heading>

      <VStack align={'stretch'} gap={1}>
        <Caption>Add to</Caption>
        <Select
          selectedValue={target}
          onValueChange={setTarget}
          placeholder={'New result'}
        >
          <Select.Item label={'New result'} value={NEW_RESULT} />
          {labOrders.map((labOrder) => (
            <Select.Item
              key={labOrder.id}
              value={labOrder.id}
              label={`${labOrder.lab ?? 'Lab'} · ${formatDate(
                labOrder.created_at ?? undefined,
                'MMM D, YYYY'
              )}`}
            />
          ))}
        </Select>
        <Text fontSize={'xs'} color={'bluegrey'}>
          {isNewResult
            ? 'Creates a new result. Pick an existing lab to attach the file to it instead.'
            : 'The file will be attached to the selected lab order.'}
        </Text>
      </VStack>

      {isNewResult && (
        <VStack align={'stretch'} gap={1}>
          <Caption>Date results completed</Caption>
          <DateField
            value={completedDate}
            onChange={setCompletedDate}
            maxDate={today}
          />
          <Text fontSize={'xs'} color={'bluegrey'}>
            Defaults to today if left empty, otherwise pick the date the labs
            were completed.
          </Text>
        </VStack>
      )}

      <VStack align={'stretch'} gap={1}>
        <Caption>Lab document</Caption>
        {file ? (
          <HStack
            justify={'space-between'}
            align={'center'}
            gap={3}
            p={3}
            rounded={'lg'}
            borderWidth={1}
            borderColor={'cloud.200'}
            bg={'white'}
          >
            <Text fontSize={'sm'} color={'secondary.500'} lineClamp={1}>
              {file.name}
            </Text>
            <Pressable
              aria-label={'Remove file'}
              color={'bluegrey'}
              onClick={() => setFile(null)}
            >
              <XIcon size={16} />
            </Pressable>
          </HStack>
        ) : (
          <Box
            as={'label'}
            display={'flex'}
            flexDirection={'column'}
            alignItems={'center'}
            justifyContent={'center'}
            gap={2}
            px={5}
            py={8}
            rounded={'lg'}
            borderWidth={1.5}
            borderStyle={'dashed'}
            borderColor={'cloud.200'}
            bg={'cloud.50'}
            cursor={'pointer'}
            onDrop={(e) => {
              e.preventDefault();
              pickFile(e.dataTransfer.files);
            }}
            onDragOver={(e) => e.preventDefault()}
          >
            <Box color={'bluegrey'}>
              <UploadIcon size={20} />
            </Box>
            <Text fontSize={'xs'} color={'bluegrey'}>
              Drag and drop a result (PDF or JPG)
            </Text>
            <Text fontSize={'xs'} color={'aqua'} fontWeight={600}>
              or choose a file from your device
            </Text>
            <input
              type={'file'}
              accept={ACCEPTED_FILE_TYPES}
              style={{ display: 'none' }}
              onChange={(e) => {
                pickFile(e.target.files);
                // Clear so re-selecting the same file fires onChange again.
                e.target.value = '';
              }}
            />
          </Box>
        )}
      </VStack>

      {mutation.isError && (
        <ErrorMessage
          message={
            (mutation.error as Error)?.message ??
            'Something went wrong uploading the result.'
          }
        />
      )}

      <HStack justify={'flex-end'} gap={2}>
        <Button variant={'link'} onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleUpload} disabled={!file || mutation.isLoading}>
          Upload result
        </Button>
      </HStack>
    </VStack>
  );
};

export default LabResultUploadForm;
